import { HttpTransportType, HubConnectionBuilder } from "@microsoft/signalr";
import Button from "components/base/Button";
import SearchBox from "components/common/SearchBox";
import React, { useEffect, useState } from "react";
import { Modal, Table } from "react-bootstrap";
import Badge from "react-bootstrap/Badge";
import { FixedSizeList as List } from "react-window";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faCopy, faCheck } from "@fortawesome/free-solid-svg-icons";
import { format } from "date-fns";
import { Monitor, Server } from "lucide-react";

const Row = ({ index, style, data, onMessageClick }: any) => {
  const log = data[index];

  // Subtle color definitions based on log level
  const getLogLevelInfo = (level: number) => {
    if (level === 4 || level === 5) {
      return { color: "#dc3545", icon: "●", name: "Error" };
    } else if (level === 3) {
      return { color: "#ffc107", icon: "●", name: "Warning" };
    } else if (level === 2) {
      return { color: "#0d6efd", icon: "●", name: "Info" };
    } else if (level === 1) {
      return { color: "#6f42c1", icon: "●", name: "Debug" };
    } else {
      return { color: "#20c997", icon: "●", name: "Trace" };
    }
  };

  const logInfo = getLogLevelInfo(log.level);
  const isEven = index % 2 === 0; // Linhas pares

  return (
    <tr
      style={{
        ...style,
        borderLeft: `2px solid ${logInfo.color}`,
        display: "flex",
        alignItems: "center",
      }}
      className={`w-100 ${isEven ? "table-striped-row" : ""}`}
    >
      <td style={{ width: "5%", padding: "8px" }}>{log.tipo}</td>
      <td style={{ width: "15%", padding: "8px" }}>{log.empresa?.cnpj}</td>
      <td style={{ width: "25%", padding: "8px" }}>
        {log.empresa?.razaoSocial}
      </td>
      <td style={{ width: "15%", padding: "8px" }}>{log.horario}</td>
      <td
        style={{
          whiteSpace: "nowrap",
          overflow: "hidden",
          textOverflow: "ellipsis",
          width: "40%",
          cursor: "pointer",
          padding: "8px",
        }}
        onClick={() => onMessageClick(log)} // Pass the entire log object
      >
        <div className="d-flex align-items-center">
          <span
            style={{
              color: logInfo.color,
              marginRight: "5px",
              fontSize: "10px",
            }}
          >
            {logInfo.icon}
          </span>
          {log.mensagem}
        </div>
      </td>
    </tr>
  );
};

export default function Logs() {
  const [logs, setLogs] = React.useState<
    { level: number; stackTrace: string; mensagem: string; empresa: any }[]
  >([]);
  const [connections, setConnections] = React.useState<any>(undefined);
  const listRef = React.useRef<List>(null);
  const isAtEndOfListRef = React.useRef(true);
  const [filteredLogs, setFilteredLogs] = React.useState(logs);
  const [selectedLevels, setSelectedLevels] = React.useState<number[]>([]);
  const [searchTerm, setSearchTerm] = React.useState("");

  // Modal states
  const [showModal, setShowModal] = useState(false);
  const [selectedLog, setSelectedLog] = useState<any>(null);
  const [copyMsgSuccess, setCopyMsgSuccess] = useState(false);
  const [copyStackSuccess, setCopyStackSuccess] = useState(false);

  const filteredLogsBySearch = filteredLogs?.filter(
    (log) =>
      log.empresa?.razaoSocial
        ?.toLowerCase()
        .includes(searchTerm.toLowerCase()) ||
      log.empresa?.cnpj?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      log.mensagem?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      log.stackTrace?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const handleScroll = ({
    scrollOffset,
    scrollUpdateWasRequested,
  }: {
    scrollOffset: number;
    scrollUpdateWasRequested: boolean;
  }) => {
    if (!scrollUpdateWasRequested) {
      const isAtEnd = scrollOffset >= (logs?.length ?? 1) * 50 - 550;
      isAtEndOfListRef.current = isAtEnd;
    }
  };

  useEffect(() => {
    const clients = [
      {
        url: "/ws/BackendHub",
        icon: <Server />,
      },
      {
        url: "/ws/FrontendHub",
        icon: <Monitor />,
      },
    ];
    let openConnetcions = [] as any[];
    clients.forEach((e) => {
      const c = new HubConnectionBuilder()
        .withUrl(e.url, {
          transport: HttpTransportType.WebSockets,
        })
        .build();
      c.onclose(() => {
        setTimeout(() => {
          c.start()
            .then(() => { })
            .catch(() => { });
        }, 5000);
      });
      c.on("Log", (registro: any) => {
        const newLog = {
          tipo: e.icon,
          level: registro.logLevel,
          stackTrace: registro.stackTrace,
          mensagem: registro.mensagem,
          empresa: registro.backend ?? registro.frontend,
          horario: format(new Date(registro.horario), 'dd/MM - HH:mm:ss'),
        };

        setLogs((prevLogs) => {
          const newLogs = [...prevLogs, newLog];
          return newLogs;
        });
      });
      c.start().then(() => {
        c.invoke("Logar")
          .then(() => { })
          .catch((err: any) => {
            console.error(err);
          });
      });
      openConnetcions.push(c);
    });
    setConnections(openConnetcions);
    return () => {
      connections?.forEach((e: any) => e.stop());
    };
  }, []);

  const handleMessageClick = (log: any) => {
    setSelectedLog(log);
    setShowModal(true);
  };

  const handleCloseModal = () => {
    setShowModal(false);
    setCopyMsgSuccess(false);
    setCopyStackSuccess(false);
  };

  const copyMessageToClipboard = () => {
    if (!selectedLog) return;

    navigator.clipboard
      .writeText(selectedLog.mensagem)
      .then(() => {
        setCopyMsgSuccess(true);
        setTimeout(() => setCopyMsgSuccess(false), 2000);
      })
      .catch((err) => console.error("Could not copy message: ", err));
  };

  const copyStackTraceToClipboard = () => {
    if (!selectedLog || !selectedLog.stackTrace) return;

    navigator.clipboard
      .writeText(selectedLog.stackTrace)
      .then(() => {
        setCopyStackSuccess(true);
        setTimeout(() => setCopyStackSuccess(false), 2000);
      })
      .catch((err) => console.error("Could not copy stack trace: ", err));
  };

  const handleBadgeClick = (levels: number[]) => {
    if (
      JSON.stringify(levels.sort()) === JSON.stringify(selectedLevels.sort())
    ) {
      setSelectedLevels([]); // Desseleciona a badge
      setFilteredLogs(logs); // Mostra todos os logs novamente
    } else {
      setSelectedLevels(levels); // Seleciona a nova badge
      const newFilteredLogs = logs.filter((log) => levels.includes(log.level));
      setFilteredLogs(newFilteredLogs);
    }
  };

  useEffect(() => {
    const newFilteredLogs = logs.filter(
      (log) => selectedLevels.includes(log.level) || selectedLevels.length === 0
    );
    setFilteredLogs(newFilteredLogs);

    if (listRef.current !== null && isAtEndOfListRef.current) {
      setTimeout(() => {
        if (newFilteredLogs.length > 0) {
          listRef.current?.scrollToItem(newFilteredLogs.length - 1, "end");
        }
      }, 0);
    }
  }, [logs, selectedLevels]);

  // Get color by log level for consistent styling
  const getLogLevelInfo = (level: number) => {
    if (level === 4 || level === 5) {
      return {
        color: "#dc3545",
        bgColor: "rgba(220, 53, 69, 0.1)",
        name: "Error",
      };
    } else if (level === 3) {
      return {
        color: "#ffc107",
        bgColor: "rgba(255, 193, 7, 0.1)",
        name: "Warning",
      };
    } else if (level === 2) {
      return {
        color: "#0d6efd",
        bgColor: "rgba(13, 110, 253, 0.1)",
        name: "Info",
      };
    } else if (level === 1) {
      return {
        color: "#6f42c1",
        bgColor: "rgba(111, 66, 193, 0.1)",
        name: "Debug",
      };
    } else {
      return {
        color: "#20c997",
        bgColor: "rgba(32, 201, 151, 0.1)",
        name: "Trace",
      };
    }
  };

  const isLevelSelected = (levels: number[]) => {
    return levels.some((level) => selectedLevels.includes(level));
  };

  return (
    <div className="w-100 justify-content-between">
      <div className="w-100 d-flex justify-content-end m-2 ms-0">
        <SearchBox
          className="w-100"
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
        />

        <Button
          className={`d-flex ms-2 me-2 align-items-center ${isLevelSelected([0]) ? "active" : ""
            }`}
          onClick={() => handleBadgeClick([0])}
          variant="outline-secondary"
          style={{
            borderColor: getLogLevelInfo(0).color,
            color: isLevelSelected([0]) ? "#fff" : getLogLevelInfo(0).color,
            backgroundColor: isLevelSelected([0])
              ? getLogLevelInfo(0).color
              : "transparent",
          }}
        >
          Trace
          <div
            className="ms-2 p-1 rounded"
            style={{
              backgroundColor: isLevelSelected([0])
                ? "rgba(255, 255, 255, 0.3)"
                : "rgba(32, 201, 151, 0.2)",
              minWidth: "24px",
              textAlign: "center",
            }}
          >
            {logs.filter((e) => e.level === 0)?.length ?? 0}
          </div>
        </Button>

        <Button
          className={`d-flex me-2 align-items-center ${isLevelSelected([1]) ? "active" : ""
            }`}
          onClick={() => handleBadgeClick([1])}
          variant="outline-secondary"
          style={{
            borderColor: getLogLevelInfo(1).color,
            color: isLevelSelected([1]) ? "#fff" : getLogLevelInfo(1).color,
            backgroundColor: isLevelSelected([1])
              ? getLogLevelInfo(1).color
              : "transparent",
          }}
        >
          Debug
          <div
            className="ms-2 p-1 rounded"
            style={{
              backgroundColor: isLevelSelected([1])
                ? "rgba(255, 255, 255, 0.3)"
                : "rgba(111, 66, 193, 0.2)",
              minWidth: "24px",
              textAlign: "center",
            }}
          >
            {logs.filter((e) => e.level === 1)?.length ?? 0}
          </div>
        </Button>

        <Button
          className={`d-flex me-2 align-items-center ${isLevelSelected([2]) ? "active" : ""
            }`}
          onClick={() => handleBadgeClick([2])}
          variant="outline-secondary"
          style={{
            borderColor: getLogLevelInfo(2).color,
            color: isLevelSelected([2]) ? "#fff" : getLogLevelInfo(2).color,
            backgroundColor: isLevelSelected([2])
              ? getLogLevelInfo(2).color
              : "transparent",
          }}
        >
          Info
          <div
            className="ms-2 p-1 rounded"
            style={{
              backgroundColor: isLevelSelected([2])
                ? "rgba(255, 255, 255, 0.3)"
                : "rgba(13, 110, 253, 0.2)",
              minWidth: "24px",
              textAlign: "center",
            }}
          >
            {logs.filter((e) => e.level === 2)?.length ?? 0}
          </div>
        </Button>

        <Button
          className={`d-flex me-2 align-items-center ${isLevelSelected([3]) ? "active" : ""
            }`}
          onClick={() => handleBadgeClick([3])}
          variant="outline-secondary"
          style={{
            borderColor: getLogLevelInfo(3).color,
            color: isLevelSelected([3]) ? "#fff" : getLogLevelInfo(3).color,
            backgroundColor: isLevelSelected([3])
              ? getLogLevelInfo(3).color
              : "transparent",
          }}
        >
          Warning
          <div
            className="ms-2 p-1 rounded"
            style={{
              backgroundColor: isLevelSelected([3])
                ? "rgba(255, 255, 255, 0.3)"
                : "rgba(255, 193, 7, 0.2)",
              minWidth: "24px",
              textAlign: "center",
            }}
          >
            {logs.filter((e) => e.level === 3)?.length ?? 0}
          </div>
        </Button>

        <Button
          className={`d-flex me-2 align-items-center ${isLevelSelected([4, 5]) ? "active" : ""
            }`}
          onClick={() => handleBadgeClick([4, 5])}
          variant="outline-secondary"
          style={{
            borderColor: getLogLevelInfo(4).color,
            color: isLevelSelected([4, 5]) ? "#fff" : getLogLevelInfo(4).color,
            backgroundColor: isLevelSelected([4, 5])
              ? getLogLevelInfo(4).color
              : "transparent",
          }}
        >
          Error
          <div
            className="ms-2 p-1 rounded"
            style={{
              backgroundColor: isLevelSelected([4, 5])
                ? "rgba(255, 255, 255, 0.3)"
                : "rgba(220, 53, 69, 0.2)",
              minWidth: "24px",
              textAlign: "center",
            }}
          >
            {logs.filter((e) => e.level === 4 || e.level === 5)?.length ?? 0}
          </div>
        </Button>
      </div>
      <div className="mx-n4 px-4 mx-lg-n6 px-lg-6 bg-body-emphasis border-top border-bottom border-translucent position-relative">
        <Table className="w-100 phoenix-table table mb-0">
          <thead>
            <tr>
              <th style={{ width: "5%" }}>Tipo</th>
              <th style={{ width: "15%" }}>CNPJ</th>
              <th style={{ width: "25%" }}>Razão Social</th>
              <th style={{ width: "15%" }}>Horário</th>
              <th style={{ width: "40%" }}>Mensagem</th>
            </tr>
          </thead>
        </Table>
        <div style={{ width: "100%", overflow: "hidden" }}>
          <List
            ref={listRef}
            height={550}
            itemCount={filteredLogsBySearch?.length ?? 0}
            itemSize={60}
            width="100%"
            itemData={filteredLogsBySearch}
            style={{
              overflow: "auto",
              borderRadius: "4px",
            }}
            onScroll={handleScroll}
            overscanCount={20}
          >
            {(props: any) => (
              <Row {...props} onMessageClick={handleMessageClick} />
            )}
          </List>
        </div>
        <div id="logs"></div>
      </div>

      {/* Log Detail Modal */}
      <Modal show={showModal} onHide={handleCloseModal} size="lg">
        <Modal.Header closeButton>
          <Modal.Title>
            <div className="d-flex align-items-center">
              {selectedLog?.tipo && (
                <span className="me-2">{selectedLog.tipo}</span>
              )}
              Log Details
              {selectedLog && (
                <Badge
                  bg={
                    selectedLog.level === 4 || selectedLog.level === 5
                      ? "danger"
                      : selectedLog.level === 3
                        ? "warning"
                        : selectedLog.level === 2
                          ? "primary"
                          : selectedLog.level === 1
                            ? "secondary"
                            : "success"
                  }
                  className="ms-2"
                >
                  {selectedLog.level === 4 || selectedLog.level === 5
                    ? "Error"
                    : selectedLog.level === 3
                      ? "Warning"
                      : selectedLog.level === 2
                        ? "Info"
                        : selectedLog.level === 1
                          ? "Debug"
                          : "Trace"}
                </Badge>
              )}
            </div>
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {selectedLog && (
            <div>
              <div className="mb-3">
                <h6 className="mb-1">Empresa</h6>
                <div className="row">
                  <div className="col-md-6">
                    <strong>CNPJ:</strong> {selectedLog.empresa?.cnpj || "N/A"}
                  </div>
                  <div className="col-md-6">
                    <strong>Razão Social:</strong>{" "}
                    {selectedLog.empresa?.razaoSocial || "N/A"}
                  </div>
                </div>
              </div>

              <div className="mb-3">
                <h6 className="mb-1">Horário</h6>
                <p>{selectedLog.horario}</p>
              </div>

              <div className="mb-3">
                <h6 className="mb-1">Mensagem</h6>
                <div className="p-3 border rounded position-relative bg-body-secondary">
                  <div
                    style={{
                      position: "absolute",
                      top: "6px",
                      right: "6px",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      width: "24px",
                      height: "24px",
                      backgroundColor: "var(--bs-secondary-bg)",
                      borderRadius: "4px",
                      boxShadow: "0 0 5px rgba(0, 0, 0, 0.1)",
                      zIndex: 1,
                    }}
                  >
                    <Button
                      variant="link"
                      className="p-0"
                      style={{ color: "var(--bs-secondary-color)" }}
                      onClick={copyMessageToClipboard}
                      title={
                        copyMsgSuccess ? "Mensagem copiada!" : "Copiar mensagem"
                      }
                    >
                      <FontAwesomeIcon
                        icon={copyMsgSuccess ? faCheck : faCopy}
                        size="xs"
                      />
                    </Button>
                  </div>
                  <pre
                    className="text-body mb-0"
                    style={{
                      whiteSpace: "pre-wrap",
                      wordBreak: "break-word",
                      marginRight: "20px", // Add space for button
                    }}
                  >
                    {selectedLog.mensagem}
                  </pre>
                </div>
              </div>

              {selectedLog.stackTrace && (
                <div className="mb-3">
                  <h6 className="mb-1">Stack Trace</h6>
                  <div
                    className="p-3 bg-body-secondary border rounded position-relative"
                    style={{ maxHeight: "300px", overflow: "auto" }}
                  >
                    <div
                      style={{
                        position: "absolute",
                        top: "6px",
                        right: "6px",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        width: "24px",
                        height: "24px",
                        backgroundColor: "var(--bs-secondary-bg)",
                        borderRadius: "4px",
                        boxShadow: "0 0 5px rgba(0, 0, 0, 0.1)",
                        zIndex: 1,
                      }}
                    >
                      <Button
                        variant="link"
                        className="p-0"
                        style={{ color: "var(--bs-secondary-color)" }}
                        onClick={copyStackTraceToClipboard}
                        title={
                          copyStackSuccess
                            ? "Stack trace copiado!"
                            : "Copiar stack trace"
                        }
                      >
                        <FontAwesomeIcon
                          icon={copyStackSuccess ? faCheck : faCopy}
                          size="xs"
                        />
                      </Button>
                    </div>
                    <pre
                      className="text-body mb-0"
                      style={{
                        whiteSpace: "pre-wrap",
                        wordBreak: "break-word",
                        marginRight: "20px", // Add space for button
                      }}
                    >
                      {selectedLog.stackTrace}
                    </pre>
                  </div>
                </div>
              )}
            </div>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={handleCloseModal}>
            Fechar
          </Button>
        </Modal.Footer>
      </Modal>
    </div>
  );
}
